/**
 * Контекст режима рабочего пространства (E4.5): какой контур показан, manifest учебного
 * экземпляра и переходы между контурами. Вне провайдера -- рабочий контур.
 */

import { createContext, useCallback, useContext } from 'react';
import type { TrainingManifest } from '@/api/training';
import { LIVE_MODE, type WorkspaceMode } from '@/features/training/types';
import { workspaceHref } from './workspaceRoutes';

export interface WorkspaceDataSourceValue {
	mode: WorkspaceMode;
	/** Manifest учебного экземпляра; null в рабочем контуре */
	manifest: TrainingManifest | null;
	/** Открыть учебный экземпляр: адрес получает его ID, manifest загружается до страницы */
	enterTraining: (instanceId: string, to?: string) => void;
	/** Вернуться к работе (на обзор или по адресу): переход проходит проверку незавершённых изменений */
	exitTraining: (to?: string) => void;
	/** Начать пример заново: состояние экземпляра сбрасывается, кеш учебного контура -- тоже */
	resetTraining: () => Promise<void>;
}

const noop = () => {};

export const WorkspaceDataSourceContext = createContext<WorkspaceDataSourceValue>({
	mode: LIVE_MODE,
	manifest: null,
	enterTraining: noop,
	exitTraining: noop,
	resetTraining: async () => {},
});

export function useWorkspaceDataSource(): WorkspaceDataSourceValue {
	return useContext(WorkspaceDataSourceContext);
}

/** Адрес в текущем режиме -- для ссылок на ресурсы, события, политики и LLM */
export function useWorkspaceHref(): (to: string) => string {
	const { mode } = useWorkspaceDataSource();
	return useCallback((to: string) => workspaceHref(to, mode), [mode]);
}
